/**
 * AccountTypeSelector Component
 * 
 * Provides a selector for account types with icons and descriptions.
 * 
 * Features:
 * - Visual account type selection
 * - Icons for each account type
 * - Descriptions for accessibility
 * - WCAG 2.2 AA compliance
 * 
 * @see .specify/memory/constitution.md
 * @see /Reference/Reference design-review/design-principles.md
 */

import React from 'react';
import { AccountType } from '../../types/account.types';
import { 
  Wallet, 
  PiggyBank, 
  CreditCard, 
  TrendingUp, 
  Building2,
  Home
} from 'lucide-react';

interface AccountTypeSelectorProps {
  value: AccountType;
  onChange: (type: AccountType) => void;
  disabled?: boolean;
  error?: string;
}

export function AccountTypeSelector({ 
  value, 
  onChange, 
  disabled = false,
  error 
}: AccountTypeSelectorProps): React.ReactElement {
  const accountTypes = [
    {
      type: AccountType.BANK_CASH,
      label: 'Bank/Cash',
      description: 'Checking, savings, and cash accounts',
      icon: Wallet,
    },
    {
      type: AccountType.SAVINGS,
      label: 'Savings',
      description: 'High-yield savings and term deposits',
      icon: PiggyBank,
    },
    {
      type: AccountType.CREDIT_CARD,
      label: 'Credit Card',
      description: 'Credit cards and charge cards',
      icon: CreditCard,
    },
    {
      type: AccountType.LOAN,
      label: 'Loan',
      description: 'Personal loans, mortgages, novated leases, and debt',
      icon: TrendingUp,
    },
    {
      type: AccountType.INVESTMENTS_SUPERANNUATION,
      label: 'Investments/Super',
      description: 'Investment accounts and superannuation',
      icon: Building2,
    },
    {
      type: AccountType.ASSETS,
      label: 'Assets',
      description: 'Property, vehicles, valuables, and other assets',
      icon: Home,
    },
  ];

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        Account Type
      </label>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {accountTypes.map((accountType) => {
          const Icon = accountType.icon;
          const isSelected = value === accountType.type;
          
          return (
            <button
              key={accountType.type}
              type="button"
              onClick={() => onChange(accountType.type)}
              disabled={disabled}
              className={`
                relative p-4 border-2 rounded-lg text-left transition-all duration-200
                ${isSelected
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 hover:border-gray-300 dark:border-gray-600 dark:hover:border-gray-500'
                }
                ${disabled
                  ? 'opacity-50 cursor-not-allowed'
                  : 'cursor-pointer hover:shadow-sm'
                }
                focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
              `}
              aria-pressed={isSelected}
              aria-describedby={`${accountType.type}-description`}
            >
              <div className="flex items-start space-x-3">
                <div className={`
                  p-2 rounded-lg
                  ${isSelected
                    ? 'bg-primary-100 text-primary-600 dark:bg-primary-800 dark:text-primary-300'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                  }
                `}>
                  <Icon className="h-5 w-5" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <h3 className={`
                    text-sm font-medium
                    ${isSelected
                      ? 'text-primary-900 dark:text-primary-100'
                      : 'text-gray-900 dark:text-gray-100'
                    }
                  `}>
                    {accountType.label}
                  </h3>
                  <p 
                    id={`${accountType.type}-description`}
                    className={`
                      text-xs mt-1
                      ${isSelected
                        ? 'text-primary-700 dark:text-primary-300'
                        : 'text-gray-500 dark:text-gray-400'
                      }
                    `}
                  >
                    {accountType.description}
                  </p>
                </div>
              </div>
              
              {/* Selection indicator */}
              {isSelected && (
                <div className="absolute top-2 right-2">
                  <div className="h-4 w-4 bg-primary-600 rounded-full flex items-center justify-center">
                    <svg className="h-2.5 w-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              )}
            </button>
          );
        })}
      </div>
      
      {error && (
        <p className="text-sm text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
